import { useActiveOrganizationId } from "@/contexts"
import { ApiQueryName } from "@/models/api"
import { useQueryKey, useRenameQueryResult } from "@/services/utils"
import { httpClient, toHttpError } from "@/utils/api"
import { useQuery } from "@tanstack/react-query"

import type { SetpointScenarios } from "../pages/schedule-misalignment-route/schedule-misalignment.types"
import { useWindowQuery } from "./use-window-query"
import type { WindowQuery } from "./use-window-query"

/**
 * The E2 setpoint-scenario leaf
 * (`/neat/insights/schedule-misalignment/{site_id}/setpoint`).
 *
 * Fetched lazily, per site, because it costs ~2s to compute — eight tariff
 * evaluations over a year of intervals. Carrying it on every list row turned
 * the E2 response into an 8-second one for an analysis only the opened site
 * needs. Same untyped-leaf plumbing as `use-sankey-drill`.
 */
const getSetpointScenarios = async (
  organizationId: number,
  siteId: string,
  win: WindowQuery
): Promise<SetpointScenarios> => {
  try {
    const res = await httpClient.get<SetpointScenarios>(
      `/api/v1/neat/insights/schedule-misalignment/${siteId}/setpoint`,
      { params: { organization_id: organizationId, ...win } }
    )
    return res.data
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

export const useSetpointScenarios = (siteId: string | null) => {
  const { activeOrganizationId } = useActiveOrganizationId()
  const win = useWindowQuery()
  const queryKey = useQueryKey(ApiQueryName.SetpointScenarios, "getOne", null, {
    siteId,
    ...win,
  })
  const queryResult = useQuery<SetpointScenarios>({
    queryKey,
    enabled: activeOrganizationId != null && siteId != null,
    queryFn: () =>
      getSetpointScenarios(Number(activeOrganizationId), siteId as string, win),
  })
  return useRenameQueryResult(queryResult, ApiQueryName.SetpointScenarios)
}
